import {
  Dispatch,
  MutableRefObject,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useProjectId } from 'store/project';
import { stableSort } from 'helpers/sort';
import { getAvailableFields } from 'shared/domain/visibleField/availableFields';
import {
  useEditVisibleFields,
  useVisibleFieldsByProcess,
  useVisibleFieldsLoading,
  useVisibleFieldsReload,
} from 'store/visibleFields';
import { showChannelRequestErrorToast } from 'services/toaster';
import { useWorkflows } from 'store/workflow';
import { useProcesses } from 'store/process';
import { useAppNavigation } from 'contexts/navigation';
import { SingleChoiceValue } from 'components/common/simpleInputFields/singleChoice/types';
import { IssueFieldNames } from 'shared/domain/issueForm/types/fieldNames';
import { HashMap, LabelledEntity } from 'shared/types/commonView';
import { ProcessType } from 'shared/domain/process/types/model';
import {
  findSelectedProcess,
  getWorkflowIdForProcess,
  resolveSelectedWorkflowId,
  WorkflowChoice,
} from '../workflowAssignment';
import { useLatestRef } from 'hooks/useLatestRef';
import { buildVisibleFieldsEdit } from '../helpers';
import type { FieldVisibilityPresentationalProps, WorkflowCreateProps } from '../types';

export const WORKFLOW_RETURN_TO_FIELDS_VISIBILITY = 'fieldsVisibility';

const SPLIT_CHAR = ',';

export function useFieldVisibilityModel(): FieldVisibilityPresentationalProps {
  const navigation = useAppNavigation();
  const settersRef = useRef<HashMap<Dispatch<SetStateAction<boolean>>>>({});
  const containerRef = useRef<HTMLDivElement>(null);
  const projectId = useProjectId();
  const { processes, updateWorkflow } = useProcesses();
  const { workflows, createWorkflow, reload: reloadWorkflows } = useWorkflows();
  const fields = useMemo(() => getAvailableFields(), []);

  useEffect(() => {
    void reloadWorkflows();
  }, [reloadWorkflows]);

  const [createOpen, setCreateOpen] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createLoading, setCreateLoading] = useState(false);
  // The process the user chose; the first one until then, also when processes load later.
  const [chosenProcessId, setChosenProcessId] = useState<string>();
  const selectedProcess = findSelectedProcess(processes, chosenProcessId);
  const processRef = useLatestRef(selectedProcess);
  const fieldVisibility = useVisibleFieldsByProcess();
  const loading = useVisibleFieldsLoading();
  const reloadVisibleFields = useVisibleFieldsReload();
  const editVisibleFields = useEditVisibleFields();
  const [formState, setFormState] = useState<Record<string, boolean>>({});
  const [defaultDaysState, setDefaultDaysState] = useState<
    Record<string, number | null | undefined>
  >({});
  const [isDirty, setIsDirty] = useState(false);
  const [workflowSubmitting, setWorkflowSubmitting] = useState(false);
  // Kept while the workflows reload (e.g. right after opening): only saving or cancelling drops it.
  const [workflowChoice, setWorkflowChoice] = useState<WorkflowChoice>();
  const selectedWorkflowId = resolveSelectedWorkflowId(
    workflowChoice,
    processes,
    selectedProcess?._id,
    workflows
  );

  const workflowOptions: LabelledEntity[] = useMemo(() => {
    const sorted = stableSort([...workflows], (a, b) =>
      a.name.localeCompare(b.name)
    );
    return sorted.map((w) => ({ _id: w._id, label: w.name }));
  }, [workflows]);

  const selectedWorkflowOption = useMemo((): LabelledEntity | undefined => {
    if (workflowOptions.length === 0) {
      return undefined;
    }
    const id = selectedWorkflowId ?? workflowOptions[0]._id;
    return workflowOptions.find((o) => o._id === id) ?? workflowOptions[0];
  }, [workflowOptions, selectedWorkflowId]);

  const onWorkflowChange = useCallback(
    (_: unknown, value?: SingleChoiceValue) => {
      if (value?._id && selectedProcess) {
        setWorkflowChoice({ processId: selectedProcess._id, workflowId: value._id });
        setIsDirty(true);
      }
    },
    [selectedProcess]
  );

  const handleSubmit = useCallback(async () => {
    try {
      setWorkflowSubmitting(true);
      const hasVisibleChanges =
        Object.keys(formState).length > 0 ||
        Object.keys(defaultDaysState).length > 0;
      if (hasVisibleChanges) {
        await editVisibleFields.execute(
          buildVisibleFieldsEdit({
            formState,
            defaultDaysState,
            visibleFieldsByProcess: fieldVisibility,
            splitChar: SPLIT_CHAR,
          })
        );
      }
      const processId = selectedProcess?._id;
      const serverWorkflowId = getWorkflowIdForProcess(processes, processId);
      if (
        processId &&
        selectedWorkflowId !== undefined &&
        selectedWorkflowId !== serverWorkflowId &&
        projectId
      ) {
        await updateWorkflow.execute(processId, selectedWorkflowId, projectId);
      }
      setIsDirty(false);
      setFormState({});
      setDefaultDaysState({});
      setWorkflowChoice(undefined);
      containerRef.current?.parentElement?.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    } catch (error) {
      showChannelRequestErrorToast('Error while saving the field visibility', error);
    } finally {
      setWorkflowSubmitting(false);
    }
  }, [
    updateWorkflow,
    defaultDaysState,
    editVisibleFields,
    fieldVisibility,
    formState,
    processes,
    projectId,
    selectedProcess?._id,
    selectedWorkflowId,
  ]);

  const handleCancel = useCallback(() => {
    setIsDirty(false);
    setFormState({});
    setDefaultDaysState({});
    setWorkflowChoice(undefined);
    void reloadVisibleFields();
    containerRef.current?.parentElement?.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }, [reloadVisibleFields]);

  const available = useMemo(
    () =>
      processes.map((process: ProcessType) => ({
        _id: process._id,
        label: process.label,
      })) as LabelledEntity[],
    [processes]
  );

  const onChange = useCallback((event: { target: { id: string } }, value: boolean) => {
    const fieldName = event.target.id;
    const processId = processRef.current?._id;
    if (!processId) return;
    const formKey = `${fieldName}${SPLIT_CHAR}${processId}`;
    setFormState((prev) => ({ ...prev, [formKey]: value }));
    setIsDirty(true);
    if (
      settersRef.current[fieldName] &&
      typeof settersRef.current[fieldName] === 'function'
    ) {
      settersRef.current[fieldName](value);
    }
  }, []);

  const onDefaultDaysChange = useCallback(
    (fieldName: IssueFieldNames, value: number | undefined) => {
      const processId = processRef.current?._id;
      if (!processId) return;
      const formKey = `${fieldName}${SPLIT_CHAR}${processId}`;
      setDefaultDaysState((prev) => ({
        ...prev,
        [formKey]: value === undefined ? null : value,
      }));
      setIsDirty(true);
    },
    []
  );

  const onProcessChange = useCallback(
    (_: unknown, value?: SingleChoiceValue) => {
      setChosenProcessId(value?._id);
    },
    []
  );

  const resetCreate = useCallback((): void => {
    setCreateOpen(false);
    setCreateName('');
  }, []);

  const handleSubmitCreate = useCallback(async (): Promise<void> => {
    const name = createName.trim();
    if (!name || !projectId || createLoading) return;
    setCreateLoading(true);
    try {
      const workflow = await createWorkflow.execute({ name }, projectId);
      if (selectedProcess?._id) {
        await updateWorkflow.execute(selectedProcess._id, workflow._id, projectId);
        setWorkflowChoice({ processId: selectedProcess._id, workflowId: workflow._id });
      }
      resetCreate();
      navigation.goToWorkflowCreator(projectId, workflow._id, {
        state: { returnTo: WORKFLOW_RETURN_TO_FIELDS_VISIBILITY },
      });
    } catch {
      //
    } finally {
      setCreateLoading(false);
    }
  }, [
    createName,
    projectId,
    createLoading,
    createWorkflow,
    selectedProcess,
    updateWorkflow,
    resetCreate,
    navigation,
  ]);

  const handleEditWorkflow = useCallback((): void => {
    if (!projectId || !selectedWorkflowId) return;
    navigation.goToWorkflowCreator(projectId, selectedWorkflowId, {
      state: { returnTo: WORKFLOW_RETURN_TO_FIELDS_VISIBILITY },
    });
  }, [navigation, projectId, selectedWorkflowId]);

  const workflowCreate: WorkflowCreateProps = useMemo(
    () => ({
      createOpen,
      createName,
      createLoading: createLoading || createWorkflow.loading,
      onOpenCreate: () => setCreateOpen(true),
      onCloseCreate: resetCreate,
      onChangeCreateName: setCreateName,
      onSubmitCreate: () => void handleSubmitCreate(),
      onEditWorkflow: handleEditWorkflow,
    }),
    [createOpen, createName, createLoading, createWorkflow.loading, resetCreate, handleSubmitCreate, handleEditWorkflow]
  );

  return {
    containerRef,
    selectedProcess,
    processes,
    available,
    onProcessChange,
    workflowOptions,
    selectedWorkflowOption,
    onWorkflowChange,
    workflowCreate,
    fieldsProps: {
      fields,
      settersRef: settersRef as MutableRefObject<HashMap<Dispatch<SetStateAction<boolean>>>>,
      formState,
      defaultDaysState,
      onChange: onChange as (event: unknown, value: boolean) => void,
      onDefaultDaysChange,
      fieldVisibility,
      loading,
    },
    isPosting: editVisibleFields.loading || workflowSubmitting,
    isDirty,
    onSubmit: () => void handleSubmit(),
    onCancel: handleCancel,
  };
}
